import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import NotFound from '../pages/PageNotFound';
import OnboardingSuccessDialog, {
  type OnboardingSuccessVariant,
} from '../pages/onboarding/shared/OnboardingSuccessDialog';
import PartnerReducer, {
  initialServices,
} from '../pages/partners/partner_form/PartnerReducer';
import PartnerContactReducer, {
  initialOnboardContact,
} from '../pages/partners/partner_form/PartnerContactReducer';
import { CanRouteAny } from '../components/UserCan';
import { useUserCanAny } from '../hooks/useUserCan';
import { CircularProgress } from '@mui/material';

const ONBOARDING_CREATE_SCOPES = 'is-admin,district-onboarding:create';
const ONBOARDING_REVIEW_SCOPES = 'is-admin,district-onboarding:review';
const ONBOARDING_SUCCESS_SCOPES =
  'is-admin,district-onboarding:review,district-onboarding:create';

const ProductPartner = lazy(
  () => import('../pages/partners/partner/ProductPartner'),
);
const SearchPartner = lazy(
  () => import('../pages/partners/searchPartner/SearchPartner'),
);
const NewPartner = lazy(
  () => import('../pages/partners/partner_form/NewPartner'),
);
const PartnerContacts = lazy(
  () => import('../pages/partners/partner_form/ParnterContacts'),
);
const AddOnDetails = lazy(
  () => import('../pages/partners/add-on/AddOnDetailsPage'),
);
const DealImportPage = lazy(
  () => import('../pages/onboarding/import/DealImportPage'),
);
const DraftManagementPage = lazy(
  () => import('../pages/onboarding/import/DraftManagementPage'),
);
const AccountChangeEntryPage = lazy(
  () => import('../pages/onboarding/account-change/AccountChangeEntryPage'),
);
const AccountChangeFlowPage = lazy(
  () => import('../pages/onboarding/account-change/AccountChangeFlowPage'),
);
const AccountChangeSuccessPage = lazy(
  () => import('../pages/onboarding/account-change/AccountChangeSuccessPage'),
);
const RedesignFlowPage = lazy(
  () => import('../pages/onboarding/account-change/RedesignFlowPage'),
);
const RedesignSuccessPage = lazy(
  () => import('../pages/onboarding/account-change/RedesignSuccessPage'),
);

// Phase 3 lazy-loaded routes
const SpecialistReviewScreen = lazy(
  () => import('../pages/onboarding/specialist/SpecialistReviewScreen'),
);
const IndividualStepper = lazy(
  () => import('../pages/onboarding/individual/IndividualStepper'),
);
const DistrictStepper = lazy(
  () => import('../pages/onboarding/district/DistrictStepper'),
);
const SchoolZoneStepper = lazy(
  () => import('../pages/onboarding/schoolzone/SchoolZoneStepper'),
);

export default function OnboardingRoutes() {
  return (
    <Routes>
      <Route index element={<OnboardingIndexRoute />} />
      <Route path='legacy' element={<LegacyProductPartnerRoute />} />
      <Route path='new/*' element={<NewOnboardingPartner />} />
      <Route path='import' element={<ImportRouteGuard />} />
      <Route
        path='import/account-change'
        element={<AccountChangeEntryRouteGuard />}
      />
      <Route
        path='import/account-change/:workflowId'
        element={<RedesignFlowRouteGuard />}
      />
      <Route
        path='import/account-change/:workflowId/success'
        element={<RedesignSuccessRouteGuard />}
      />
      <Route
        path='import/account-change/v2/:workflowId'
        element={<AccountChangeFlowRouteGuard />}
      />
      <Route
        path='import/account-change/v2/:workflowId/success'
        element={<AccountChangeSuccessRouteGuard />}
      />
      <Route path='import/draft/:workflowId' element={<DraftRouteGuard />} />
      {/* Phase 3 routes */}
      <Route
        path='import/review/:workflowId'
        element={<SpecialistReviewRouteGuard />}
      />
      <Route
        path='import/individual/:workflowId'
        element={<IndividualStepperRouteGuard />}
      />
      <Route
        path='import/district/:workflowId'
        element={<DistrictStepperRouteGuard />}
      />
      <Route
        path='import/schoolzone/:workflowId'
        element={<SchoolZoneStepperRouteGuard />}
      />
      <Route
        path='import/submitted/:workflowId'
        element={<LegacySuccessRouteGuard successVariant='submitted' />}
      />
      <Route
        path='import/success/:workflowId'
        element={<LegacySuccessRouteGuard successVariant='accounts-created' />}
      />
      <Route path=':company_id/add-on/*' element={<AddOnPartner />} />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

const OnboardingIndexRoute = () => {
  const hasImportAccess = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (hasImportAccess) {
    return <Navigate to='import' replace />;
  }

  return <LegacyProductPartnerRoute />;
};

const LegacyProductPartnerRoute = () => {
  return (
    <Suspense fallback={<CircularProgress />}>
      <ProductPartner />
    </Suspense>
  );
};

const ImportRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);
  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <DealImportPage />
    </Suspense>
  );
};

const DraftRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);
  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <DraftManagementPage />
    </Suspense>
  );
};

const AccountChangeEntryRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <AccountChangeEntryPage />
    </Suspense>
  );
};

const AccountChangeFlowRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <AccountChangeFlowPage />
    </Suspense>
  );
};

const AccountChangeSuccessRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_SUCCESS_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_SUCCESS_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <AccountChangeSuccessPage />
    </Suspense>
  );
};

const RedesignFlowRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <RedesignFlowPage />
    </Suspense>
  );
};

const RedesignSuccessRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_SUCCESS_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_SUCCESS_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <RedesignSuccessPage />
    </Suspense>
  );
};

/**
 * Phase 3 route guard for specialist review screen.
 * Gated behind `district-onboarding:review` scope.
 */
const SpecialistReviewRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_REVIEW_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_REVIEW_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <SpecialistReviewScreen />
    </Suspense>
  );
};

/**
 * Phase 3 route guard for individual and district stepper screens.
 * Gated behind `district-onboarding:create` scope (product representatives).
 */
const IndividualStepperRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <IndividualStepper />
    </Suspense>
  );
};

const DistrictStepperRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <DistrictStepper />
    </Suspense>
  );
};

const SchoolZoneStepperRouteGuard = () => {
  const hasPermission = useUserCanAny(ONBOARDING_CREATE_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_CREATE_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }
  return (
    <Suspense fallback={<CircularProgress />}>
      <SchoolZoneStepper />
    </Suspense>
  );
};

const LegacySuccessRouteGuard = ({
  successVariant,
}: {
  successVariant: OnboardingSuccessVariant;
}) => {
  const hasPermission = useUserCanAny(ONBOARDING_SUCCESS_SCOPES);

  if (!hasPermission) {
    return (
      <CanRouteAny scope={ONBOARDING_SUCCESS_SCOPES}>
        <></>
      </CanRouteAny>
    );
  }

  return <OnboardingSuccessDialog variant={successVariant} />;
};

const NewOnboardingPartner = () => {
  const [state, dispatch] = React.useReducer(PartnerReducer, initialServices);
  const [contactState, contactDispatch] = React.useReducer(
    PartnerContactReducer,
    initialOnboardContact,
  );

  return (
    <Routes>
      <Route
        path=''
        element={
          <SearchPartner
            dispatch={dispatch}
            contactDispatch={contactDispatch}
          />
        }
      />
      <Route
        path='form'
        element={<NewPartner state={state} dispatch={dispatch} />}
      />
      <Route
        path='contacts'
        element={
          <PartnerContacts
            onBoardState={state}
            onBoardDispatch={dispatch}
            contactState={contactState}
            contactDispatch={contactDispatch}
          />
        }
      />
    </Routes>
  );
};

const AddOnPartner = () => {
  const [state, dispatch] = React.useReducer(PartnerReducer, initialServices);
  const [contactState, contactDispatch] = React.useReducer(
    PartnerContactReducer,
    initialOnboardContact,
  );

  return (
    <Routes>
      <Route path='' element={<AddOnDetails />} />
      <Route
        path='form'
        element={<NewPartner state={state} dispatch={dispatch} />}
      />
      <Route
        path='contacts'
        element={
          <PartnerContacts
            onBoardState={state}
            onBoardDispatch={dispatch}
            contactState={contactState}
            contactDispatch={contactDispatch}
          />
        }
      />
    </Routes>
  );
};
